import React, {FC, useEffect, useState} from "react"

import {Col, Row} from "react-bootstrap"
import {OnboardingUserProgress, PaymentCurrencyType, Wallet, WalletEntityType} from "@irati/types"
import authentication from "@irati/auth"
import walletService from "@irati/wallet/graph/wallet"
import Loading from "@irati/ui/src/Loading"
import onboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import Spacing from "@irati/ui/src/Spacing"
import {adminDashboardTracking} from "@irati-tracking/admin"
import Container from "../styled/Container"
import Intro from "../../components/AdminReward/Wallet/Intro"
import HeaderBalance from "../../components/AdminReward/Wallet/HeaderBalance"
import Transactions from "../../components/AdminReward/Transactions"
import Reporting from "../../components/AdminReward/Reporting"
import OnboardingSection from "../../components/OnboardingSection"
import RewardsOfferTopBanner from "../../components/RewardsOfferTopBanner"

interface State {
  wallet: Wallet
  load?: Promise<any> | null
}
const AdminRewardWallet: FC<State> = () => {
  const [wallet, setWallet] = useState<Wallet>({})
  const [load, setLoad] = useState(null)
  const [showAdminOnboardingSection, setShowAdminOnboardingSection] = useState<boolean>(true)
  const [showManagerOnboardingSection, setShowManagerOnboardingSection] = useState<boolean>(true)
  const canTopUp = !!authentication.apps("wallet.flexiRewards.canTopUpRewardPoints")

  useEffect(() => {
    fetchWallet()

    onboardingService.get(authentication.user().uuid).then((userOnboarding) => {
      if (canTopUp) {
        checkOnboardingState(
          userOnboarding.userProgress!,
          OnboardingStep.HAS_VIEWED_FLEXI_REWARDS_ADMIN_ONBOARDING,
          setShowAdminOnboardingSection
        )
      } else {
        checkOnboardingState(
          userOnboarding.userProgress!,
          OnboardingStep.HAS_VIEWED_FLEXI_REWARDS_MANAGER_ONBOARDING,
          setShowManagerOnboardingSection
        )
      }
    })
  }, [])

  const checkOnboardingState = (
    userProgress: OnboardingUserProgress[],
    onboardingStep: OnboardingStep,
    callback: (value: boolean) => void
  ) => {
    const onboardingStepStatus = !!userProgress.find(({step}) => step === onboardingStep)
    callback(onboardingStepStatus)
  }

  const fetchWallet = () => {
    const requests = walletService
      .get(authentication.user().uuid, PaymentCurrencyType.Rp, WalletEntityType.Reward, {fetchPolicy: "network-only"})
      .then((w) => setWallet(w))

    setLoad(Promise.resolve(requests))
  }

  const completeStep = () => {
    if (canTopUp) {
      onboardingService.completeStep(authentication.user(), OnboardingStep.HAS_VIEWED_FLEXI_REWARDS_ADMIN_ONBOARDING)
      setShowAdminOnboardingSection(false)
    } else {
      onboardingService.completeStep(authentication.user(), OnboardingStep.HAS_VIEWED_FLEXI_REWARDS_MANAGER_ONBOARDING)
      setShowManagerOnboardingSection(false)
    }
  }

  return (
    <Loading load={load}>
      {canTopUp && <RewardsOfferTopBanner />}
      <Container hasBanner>
        <Row>
          <Col md={12} lg={5}>
            <Intro />
          </Col>
          <Col md={12} lg={7}>
            <HeaderBalance adminRewardWallet={wallet} />
          </Col>
        </Row>
        {canTopUp && showAdminOnboardingSection && (
          <Row>
            <Col xs={12}>
              <OnboardingSection canTopUp={canTopUp} completeStep={completeStep} />
            </Col>
          </Row>
        )}
        {!canTopUp && showManagerOnboardingSection && (
          <Row>
            <Col xs={12}>
              <OnboardingSection canTopUp={canTopUp} completeStep={completeStep} />
            </Col>
          </Row>
        )}
        {canTopUp && (
          <Row>
            <Col xs={12}>
              <Spacing marginTop={8}>
                <Reporting />
              </Spacing>
            </Col>
          </Row>
        )}
        {wallet.uuid && (
          <Row>
            <Col xs={12}>
              <Transactions walletId={wallet.uuid} />
            </Col>
          </Row>
        )}
      </Container>
    </Loading>
  )
}

export default AdminRewardWallet
